import { DateTime } from 'luxon';
import { PrimaryTheme } from '@deckee/deck-hand';
import { capitalize } from '../../../components/helpers/string-helpers';
import { FLOAT_PLAN__STATUS } from '../../../configurations/constants';
import { callEndpoint } from '../../backend';

/*
 * converts the float-plan data from the entity-store into the same
 * data structure as the float-plan redux store
 */
export const mapFloatPlanToReduxState = (floatPlan) => ({
  id: floatPlan.id,
  share_token: floatPlan.shareToken,
  cancelled: floatPlan.cancelled,
  loloEnabled: floatPlan.loloEnabled,
  isDiving: floatPlan.isDiving,
  owner: {
    ...floatPlan.owner,
  },
  vessel: {
    ...floatPlan.watercraft,
  },
  trip: {
    ...floatPlan.trip,
  },
  share_contact_email: floatPlan.sharedToEmail,
  share_contact: floatPlan.shareContacts[0],
  time_type: floatPlan.timeType,
  start_time: DateTime.fromISO(floatPlan.startTime, { zone: 'utc' })
    .toLocal()
    .toISO(),
  end_time: DateTime.fromISO(floatPlan.endTime, { zone: 'utc' })
    .toLocal()
    .toISO(),
  ended_time: DateTime.fromISO(floatPlan.endedTime, { zone: 'utc' })
    .toLocal()
    .toISO(),
  people_aboard: floatPlan.numberOnBoard,
  radio_type: floatPlan.radioType,
  end_waypoint: floatPlan.endPointLatLon,
  end_waypoint_name: floatPlan.endPointName,
  start_waypoint: floatPlan.startPointLatLon,
  start_waypoint_name: floatPlan.startPointName,
  waypoints: floatPlan.waypoints,
  contacts: floatPlan.contacts,
});

export const getActiveFloatPlan = async () => {
  // see if we can find an active on against the db and then cache it for later use
  const { data } = await callEndpoint(
    callEndpoint.endpoints.getActiveFloatPlan,
  );

  if (data && Object.values(data).length > 0) {
    const floatPlan = mapFloatPlanToReduxState(data);

    return floatPlan;
  }

  return null;
};

export const createPlan = async (floatPlan) => {
  const data = await callEndpoint(callEndpoint.endpoints.createFloatPlan, {
    id: floatPlan.id,
    sharedToEmail: floatPlan.share_contact_email,
    shareContactId: floatPlan.share_contact?.id,
    loloEnabled: floatPlan.loloEnabled,
    isDiving: floatPlan.isDiving,
    timeType: floatPlan.time_type,
    startTime: DateTime.fromISO(floatPlan.start_time, DateTime.local().zone)
      .toUTC()
      .toISO(),
    endTime: DateTime.fromISO(floatPlan.end_time, DateTime.local().zone)
      .toUTC()
      .toISO(),
    timezone: DateTime.local().toFormat('z'),
    utcOffset: +DateTime.local().toFormat('Z').replace(':30', '.5'),
    numberOnBoard: floatPlan.people_aboard,
    radioType: floatPlan.radio_type,
    watercraftId: floatPlan.watercraftId,
    startWaypoint: {
      lat: floatPlan.startPointLatLon.lat,
      lon: floatPlan.startPointLatLon.lon,
    },
    startWaypointName: floatPlan.startPointName,
    endWaypoint: {
      lat: floatPlan.endPointLatLon.lat,
      lon: floatPlan.endPointLatLon.lon,
    },
    endWaypointName: floatPlan.endPointName,
    waypoints: floatPlan.waypoints,
    contacts: floatPlan.contacts,
  });

  return data;
};

export const updateFloatPlan = async (floatPlan) => {
  const data = await callEndpoint(callEndpoint.endpoints.updateFloatPlan, {
    id: floatPlan.id,
    timeType: floatPlan.time_type,
    watercraftId: floatPlan.watercraftId,
    endTime: DateTime.fromISO(floatPlan.end_time, DateTime.local().zone)
      .toUTC()
      .toISO(),
    timezone: DateTime.local().toFormat('z'),
    utcOffset: +DateTime.local().toFormat('Z').replace(':30', '.5'),
    loloEnabled: floatPlan.loloEnabled,
    isDiving: floatPlan.isDiving,
    numberOnBoard: floatPlan.people_aboard,
    contacts: floatPlan.contacts,
    startPointLatLon: floatPlan.startPointLatLon,
    startPointName: floatPlan.startPointName,
    endPointLatLon: floatPlan.endPointLatLon,
    endPointName: floatPlan.endPointName,
    waypoints: floatPlan.waypoints,
  });

  return data;
};

export const getFloatPlan = async (id) => {
  const data = await callEndpoint(callEndpoint.endpoints.getFloatPlan, { id });

  if (data) {
    return mapFloatPlanToReduxState(data);
  }

  return null;
};

export const getFloatPlanByToken = async (token) => {
  const data = await callEndpoint(callEndpoint.endpoints.getFloatPlanByToken, {
    token,
  });

  if (data) {
    return mapFloatPlanToReduxState(data);
  }

  return null;
};

export const getFloatPlanStatus = (floatPlan) => {
  if (floatPlan.cancelled) {
    return FLOAT_PLAN__STATUS.cancelled;
  }

  if (floatPlan.ended_time != null) {
    return FLOAT_PLAN__STATUS.complete;
  }

  const between =
    DateTime.now() > DateTime.fromISO(floatPlan.start_time) &&
    DateTime.now() < DateTime.fromISO(floatPlan.end_time);

  if (between) {
    return FLOAT_PLAN__STATUS.active;
  }

  return FLOAT_PLAN__STATUS.overdue;
};

export const getFloatPlanStatusColor = (floatPlan) => {
  switch (getFloatPlanStatus(floatPlan)) {
    case FLOAT_PLAN__STATUS.complete:
      return '#767676';
    case FLOAT_PLAN__STATUS.overdue:
      return '#B01515';
    case FLOAT_PLAN__STATUS.cancelled:
      return '#767676';
    case FLOAT_PLAN__STATUS.active:
    default:
      return PrimaryTheme.colors.success;
  }
};

export const endFloatPlan = async (floatPlanId) => {
  await callEndpoint(callEndpoint.endpoints.endFloatPlan, {
    id: floatPlanId,
  });

  return null;
};

export const cancelFloatPlan = async (floatPlanId) => {
  await callEndpoint(callEndpoint.endpoints.cancelFloatPlan, {
    id: floatPlanId,
  });

  return null;
};

export const isVesselFloatPlanReady = (vessel, isLoggingOn) => {
  if (!vessel) {
    return false;
  }
  // We do this here to let paddlecrafts with no length be valid
  if (isLoggingOn) {
    return (
      vessel.name != null &&
      vessel.name?.trim() !== '' &&
      vessel.watercraft_type != null &&
      vessel.watercraft_type?.trim() !== '' &&
      vessel.make_model != null &&
      vessel.make_model?.trim() !== ''
    );
  }
  return (
    vessel.name != null &&
    vessel.name?.trim() !== '' &&
    vessel.watercraft_type != null &&
    vessel.watercraft_type?.trim() !== '' &&
    vessel.make_model != null &&
    vessel.make_model?.trim() !== '' &&
    vessel.vessel_length != null &&
    vessel.vessel_length > 0
  );
};

export const isVesselAbleToLogOn = (vessel) =>
  vessel.name != null &&
  vessel.registration != null &&
  vessel.name?.trim() !== '' &&
  vessel.registration?.trim() !== '';

export const getWatercraftString = (vessel) =>
  `${capitalize(vessel?.watercraft_type)} - ${vessel?.name}`;
